// API-Client für NEKO-Tools

import type { Ausrichtung } from "./shared-types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

// Cache the tenant slug from the render-route-injected <meta name="x-tenant-slug"> tag.
// Read once on first use — the meta tag is rendered server-side by backend/api/render/index.ts
// (Phase 08.2) and never changes within a SPA session. The backend tenantResolver in
// backend/api/shared/tenant-resolver.ts requires this header (or a ?t= URL param) on every
// /api/foerder-rechner/*, /api/roi-simulator/*, and /api/dach-mapper/* request.
let _tenantSlug: string | null = null;
function getTenantSlug(): string {
  if (_tenantSlug !== null) return _tenantSlug;
  if (typeof document !== "undefined") {
    _tenantSlug =
      document.querySelector<HTMLMetaElement>('meta[name="x-tenant-slug"]')?.content ?? "";
  } else {
    _tenantSlug = ""; // SSR / test environment without DOM
  }
  return _tenantSlug;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const tenantSlug = getTenantSlug();
  if (tenantSlug) {
    headers["x-tenant-slug"] = tenantSlug;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  const data = await res.json();

  if (!res.ok) {
    const message = (data as { fehler?: string }).fehler ?? `HTTP ${res.status}`;
    throw new Error(message);
  }

  return data as T;
}

export const api = {
  foerderRechner: {
    calculate: (input: unknown) =>
      post("/api/foerder-rechner/calculate", input),
    submitLead: (payload: unknown) =>
      post("/api/foerder-rechner/submit-lead", payload),
  },
  roiSimulator: {
    calculate: (input: unknown) =>
      post("/api/roi-simulator/calculate", input),
    submitLead: (payload: unknown) =>
      post("/api/roi-simulator/submit-lead", payload),
  },
  dachMapper: {
    irradiance: (
      lat: number,
      lon: number,
      postleitzahl?: string,
      opts?: { neigung_grad?: number; ausrichtung?: Ausrichtung },
    ) =>
      post("/api/dach-mapper/irradiance", {
        lat,
        lon,
        ...(postleitzahl ? { postleitzahl } : {}),
        ...(opts?.neigung_grad !== undefined ? { neigung_grad: opts.neigung_grad } : {}),
        ...(opts?.ausrichtung ? { ausrichtung: opts.ausrichtung } : {}),
      }),
    combined: (input: unknown) =>
      post("/api/dach-mapper/combined", input),
    detectRoof: (lat: number, lon: number) =>
      post("/api/dach-mapper/detect-roof", { lat, lon }),
  },
  mapTiles: {
    session: () => post<{ tileUrl: string | null }>("/api/map-tiles/session", {}),
  },
  directContact: {
    submit: (payload: { name: string; telefon: string; interesse: string }) =>
      post("/api/foerder-rechner/submit-lead", {
        ...payload,
        email: `direkt-${Date.now()}@neko-lead.internal`,
        quelle: typeof window !== "undefined" ? window.location.href : "",
      }),
  },
};
